import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProjects, projectPath, type ProjectCard } from "../lib/projects";
import { formatCurrency, formatDateTime } from "../lib/utils";

export function ProjectsPage() {
  const [query, setQuery] = useState("");
  const [projects, setProjects] = useState<ProjectCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);

  const heroProjects = useMemo(() => {
    return projects.filter((project) => Boolean(project.image)).slice(0, 5);
  }, [projects]);

  useEffect(() => {
    setActiveHeroIndex(0);
  }, [heroProjects.length]);

  useEffect(() => {
    if (heroProjects.length < 2) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveHeroIndex((current) => (current + 1) % heroProjects.length);
    }, 4200);

    return () => {
      window.clearInterval(timer);
    };
  }, [heroProjects]);

  const activeHeroProject = heroProjects[activeHeroIndex] ?? null;

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("limit", "48");
        const result = await fetchProjects(params);
        if (!ignore) {
          setProjects(result.projects);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load projects.");
          setProjects([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [query]);

  return (
    <div className="pb-16">
      <section className="section-shell py-14">
        <div className="relative overflow-hidden rounded-[40px] border border-white/10 bg-slate-950 text-white">
          {activeHeroProject?.image ? (
            <img
              alt={activeHeroProject.title}
              className="absolute inset-0 h-full w-full object-cover"
              src={activeHeroProject.image}
            />
          ) : null}
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.92),rgba(2,6,23,0.6),rgba(2,6,23,0.8)),radial-gradient(circle_at_top_left,rgba(34,211,238,0.22),transparent_26%),radial-gradient(circle_at_82%_18%,rgba(215,38,56,0.18),transparent_24%)]" />
          <div className="relative grid min-h-[440px] gap-8 px-6 py-8 sm:px-8 lg:grid-cols-[minmax(0,1.05fr)_360px] lg:items-end lg:px-10 lg:py-10">
            <div className="max-w-3xl self-end">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-300">Projects</p>
              <h1 className="mt-4 max-w-2xl font-display text-4xl font-semibold tracking-tight sm:text-5xl">
                Real builds. Installed systems. Work that shows what Novatech can deliver.
              </h1>
              <p className="mt-5 max-w-xl text-sm leading-7 text-slate-200/90">
                Browse completed projects, custom integrations, and field-ready installations across electronics, power, networking, and technical delivery.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                <div className="rounded-full border border-white/14 bg-white/8 px-4 py-2 text-slate-100">
                  {projects.length} showcased projects
                </div>
                <div className="rounded-full border border-white/14 bg-white/8 px-4 py-2 text-slate-100">
                  {heroProjects.length} featured visuals
                </div>
              </div>
            </div>

            <div className="self-end rounded-[30px] border border-white/12 bg-black/25 p-4 backdrop-blur-md">
              {activeHeroProject ? (
                <>
                  <div className="overflow-hidden rounded-[24px] border border-white/10 bg-white/6">
                    <img alt={activeHeroProject.title} className="aspect-[4/3] w-full object-cover" src={activeHeroProject.image} />
                  </div>
                  <div className="mt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-300">
                      Featured project
                    </p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight">{activeHeroProject.title}</h2>
                    <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-200/85">
                      {activeHeroProject.summary || "View the full project story, visuals, and delivery context."}
                    </p>
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <p className="text-xs text-slate-300">{formatDateTime(activeHeroProject.addedOn)}</p>
                      <Link
                        className="inline-flex items-center rounded-full border border-white/14 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/16"
                        to={projectPath(activeHeroProject.slug, activeHeroProject.id)}
                      >
                        Open project
                      </Link>
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid min-h-[320px] place-items-center rounded-[24px] border border-white/10 bg-white/6 p-6 text-center text-sm text-slate-200/85">
                  Project visuals will appear here as showcase images are added.
                </div>
              )}

              {heroProjects.length > 1 ? (
                <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-5">
                  {heroProjects.map((project, index) => (
                    <button
                      className={`overflow-hidden rounded-[18px] border transition ${
                        index === activeHeroIndex
                          ? "border-cyan-300 shadow-[0_0_0_1px_rgba(103,232,249,0.35)]"
                          : "border-white/10 opacity-75 hover:opacity-100"
                      }`}
                      key={project.id}
                      onClick={() => setActiveHeroIndex(index)}
                      type="button"
                    >
                      <img alt={project.title} className="aspect-square w-full object-cover" src={project.image} />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell">
        <div className="glass-panel rounded-[32px] p-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
            <input
              className="field-control px-4 py-3 text-sm"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by project title, summary, description, or slug"
              value={query}
            />
            <div className="field-soft rounded-[22px] p-4 text-sm text-nv-slate">
              Projects show finished work, applied engineering, and delivery context.
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-8">
        {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {loading ? (
            <div className="glass-panel rounded-[32px] p-8 text-sm text-nv-slate">Loading projects...</div>
          ) : projects.length > 0 ? (
            projects.map((project) => (
              <Link className="glass-panel group overflow-hidden rounded-[32px]" key={project.id} to={projectPath(project.slug, project.id)}>
                <div className="aspect-[4/3] overflow-hidden bg-black/[0.04]">
                  {project.image ? (
                    <img alt={project.title} className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]" src={project.image} />
                  ) : (
                    <div className="grid h-full place-items-center text-sm text-nv-slate">Project visual coming soon</div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600">Project #{project.id}</p>
                    <p className="text-xs text-nv-slate">{formatDateTime(project.addedOn)}</p>
                  </div>
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight text-nv-ink">{project.title}</h2>
                  <p className="mt-3 line-clamp-4 text-sm leading-7 text-nv-slate">{project.summary || "No project summary available yet."}</p>
                  <div className="mt-5 flex items-center justify-between gap-3">
                    <div className="rounded-[20px] border border-black/10 bg-white px-4 py-3 shadow-sm dark:border-white/10 dark:bg-slate-900">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-nv-slate">Project price</p>
                      <p className="mt-1 text-base font-semibold text-nv-ink dark:text-white">{formatCurrency(project.cost)}</p>
                    </div>
                    <div className="inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-sm transition group-hover:bg-cyan-600 dark:bg-cyan-500 dark:text-slate-950 dark:group-hover:bg-cyan-400">
                      View project
                    </div>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="glass-panel rounded-[32px] p-8 text-sm text-nv-slate">No projects matched the current view.</div>
          )}
        </div>
      </section>
    </div>
  );
}
